import { useCallback } from "react";
import { useLocalization } from "@fluent/react";
import { Button, ContentCard } from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import Filters from "byzantine/src/filters";
import styles from "../../DualApproval.module.scss";
import { downloadWireImportCSV } from "../../../PaymentActivity/WireFileImport/helpers";
import type { BulkPaymentTopCardProps } from "./types";

const WireImportTopCard = ({
  dualApproval,
  handleClickViewPayments,
}: BulkPaymentTopCardProps) => {
  const { l10n } = useLocalization();
  const handleDownloadFile = useCallback(async () => {
    const wireImportFileName = dualApproval.wireImport?.fileName;
    if (!wireImportFileName) {
      return;
    }
    let readableFileName = "wire_file.csv";
    if (dualApproval.wireImport?.description) {
      readableFileName = dualApproval.wireImport.description;
    }

    await downloadWireImportCSV(wireImportFileName, readableFileName);
  }, [dualApproval.wireImport?.description, dualApproval.wireImport?.fileName]);

  const formattedDollarAmount = Filters.currency(dualApproval.dollarAmount, {
    hasDecimal: true,
  });

  const formattedDollarAmountWithSign = Filters.currency(
    -dualApproval.dollarAmount as Dollars,
    {
      hasDecimal: true,
    },
  );

  return (
    <div className="margin--top--l">
      <ContentCard kind="bordered">
        <h5 className="fontSize--m fontWeight--normal margin--bottom--xs">
          {dualApproval.wireImport?.description}
        </h5>
        <h1 className="fontColor--primary">{formattedDollarAmountWithSign}</h1>
        <hr className="margin--bottom--l margin--top--l" />

        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("label-sending")}
        </h5>
        <div className="margin--bottom--m">
          {formattedDollarAmount}{" "}
          {l10n.getString("via-payments-cta", {
            total: dualApproval.wireImport?.wireCount ?? 0,
          })}
        </div>

        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("label-scheduled-date")}
        </h5>
        <div className="margin--bottom--m">
          {l10n.getString("wire-import-processed-upon-approval")}
        </div>
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("label-payment-method")}
        </h5>
        <div className="margin--bottom--m">
          {l10n.getString("wire-file-import-method-name")}
        </div>

        <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
          {l10n.getString("label-details")}
        </h5>
        <Button
          kind="plain"
          size="s"
          onClick={handleClickViewPayments}
          testId="view-payments"
        >
          {l10n.getString("view-payments-cta", {
            total: dualApproval.wireImport?.wireCount || 0,
          })}
          <span className="narmi-icon-chevron-right" />
        </Button>
        <h5 className={cc("margin--none", styles.drawerLabelHeading)}>
          {l10n.getString("label-file")}
        </h5>

        <Button
          kind="plain"
          size="s"
          onClick={handleDownloadFile}
          disabled={!dualApproval.wireImport?.fileName}
          testId="wire-import-download-file"
        >
          <span className="narmi-icon-download" />
          <span className="margin--left--xs">
            {l10n.getString("label-download-file")}
          </span>
        </Button>
      </ContentCard>
    </div>
  );
};

export default WireImportTopCard;
